import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  User,
  listAdmins,
  registerAdmin,
  toggleAdminStatus,
  updateAdmin,
  deleteAdmin
} from '../api/auth';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';

interface AdminAccountTabProps {
  currentUser: User;
  onUserUpdate?: (user: User) => void;
  isAdministrator: boolean;
  admins: (User & { is_active?: boolean; created_at?: string })[];
  fetchAdmins: () => void;
}

const AdminAccountTab: React.FC<AdminAccountTabProps> = ({
  currentUser,
  onUserUpdate,
  isAdministrator,
  admins,
  fetchAdmins,
}) => {
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<User | null>(null);

  // 새 관리자 폼
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newRole, setNewRole] = useState<'administrator' | 'manager'>('manager');

  // 수정 폼
  const [editUsername, setEditUsername] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editRole, setEditRole] = useState<'administrator' | 'manager'>('manager');
  const [editPassword, setEditPassword] = useState('');

  const handleAddAdmin = async () => {
    if (!newUsername || !newPassword) {
      toast.error('아이디와 비밀번호를 입력해.');
      return;
    }

    try {
      await registerAdmin(newUsername, newPassword, newDisplayName || newUsername, newRole);
      toast.success('관리자가 추가되었음.');
      setShowAddModal(false);
      setNewUsername('');
      setNewPassword('');
      setNewDisplayName('');
      setNewRole('manager');
      fetchAdmins();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '관리자 추가에 실패했음.');
    }
  };

  const handleToggleStatus = async (adminId: number) => {
    try {
      const newStatus = await toggleAdminStatus(adminId);
      toast.success(newStatus ? '관리자가 활성화되었음.' : '관리자가 비활성화되었음.');
      fetchAdmins();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '상태 변경에 실패했음.');
    }
  };

  const handleEditAdmin = (admin: User) => {
    setSelectedAdmin(admin);
    setEditUsername(admin.username);
    setEditDisplayName(admin.display_name);
    setEditRole(admin.role);
    setEditPassword('');
    setShowEditModal(true);
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      const updateData: any = {};
      if (editUsername !== selectedAdmin.username) updateData.username = editUsername;
      if (editDisplayName !== selectedAdmin.display_name) updateData.display_name = editDisplayName;
      if (editRole !== selectedAdmin.role) updateData.role = editRole;
      if (editPassword) updateData.new_password = editPassword;

      if (Object.keys(updateData).length === 0) {
        toast.info('변경된 내용이 없음.');
        return;
      }

      await updateAdmin(selectedAdmin.admin_id, updateData);
      toast.success('관리자 정보가 수정되었음.');
      setShowEditModal(false);
      fetchAdmins();

      // 자기 자신을 수정한 경우 상위 컴포넌트에 알림
      if (selectedAdmin.admin_id === currentUser.admin_id && onUserUpdate) {
        onUserUpdate({
          ...currentUser,
          username: editUsername,
          display_name: editDisplayName,
          role: editRole
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '수정에 실패했음.');
    }
  };

  const handleDeleteAdmin = async (adminId: number, username: string) => {
    if (!window.confirm(`${username} 관리자를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await deleteAdmin(adminId);
      toast.success('관리자가 삭제되었음.');
      fetchAdmins();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || '삭제에 실패했음.');
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('ko-KR');
  };

  return (
    <>
      <div style={styles.actions}>
        <button
          style={styles.addButton}
          onClick={() => setShowAddModal(true)}
        >
          + 관리자 추가
        </button>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>로딩 중...</div>
        ) : admins.length === 0 ? (
          <div style={styles.emptyState}>관리자가 없음.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>아이디</th>
                <th style={styles.th}>표시 이름</th>
                <th style={styles.th}>역할</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>마지막 로그인</th>
                <th style={styles.th}>관리</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.admin_id}>
                  <td style={styles.td}>{admin.admin_id}</td>
                  <td style={styles.td}>{admin.username}</td>
                  <td style={styles.td}>{admin.display_name}</td>
                  <td style={styles.td}>
                    <span style={admin.role === 'administrator' ? styles.roleAdmin : styles.roleManager}>
                      {admin.role === 'administrator' ? '총괄관리자' : '매니저'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={admin.is_active !== false ? styles.statusActive : styles.statusInactive}>
                      {admin.is_active !== false ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td style={styles.td}>{formatDate(admin.last_login)}</td>
                  <td style={styles.td}>
                    <div style={styles.actionButtons}>
                      <button
                        style={styles.editButton}
                        onClick={() => handleEditAdmin(admin)}
                      >
                        수정
                      </button>
                      {admin.admin_id !== currentUser.admin_id && (
                        <>
                          <button
                            style={styles.toggleButton}
                            onClick={() => handleToggleStatus(admin.admin_id)}
                          >
                            {admin.is_active !== false ? '비활성화' : '활성화'}
                          </button>
                          <button
                            style={styles.deleteButton}
                            onClick={() => handleDeleteAdmin(admin.admin_id, admin.username)}
                          >
                            삭제
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 관리자 추가 모달 */}
      {showAddModal && (
        <div style={styles.modalBackdrop} onClick={() => setShowAddModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>관리자 추가</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>아이디 *</label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                style={styles.input}
                placeholder="로그인 ID"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>비밀번호 *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={styles.input}
                placeholder="비밀번호"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>표시 이름</label>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                style={styles.input}
                placeholder="표시될 이름"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>역할</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as 'administrator' | 'manager')}
                style={styles.select}
              >
                <option value="manager">매니저</option>
                <option value="administrator">총괄관리자</option>
              </select>
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowAddModal(false)}>
                취소
              </button>
              <button style={styles.submitButton} onClick={handleAddAdmin}>
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 관리자 수정 모달 */}
      {showEditModal && selectedAdmin && (
        <div style={styles.modalBackdrop} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{isAdministrator ? '관리자 수정' : '내 정보 수정'}</h3>
            {isAdministrator && (
              <div style={styles.formGroup}>
                <label style={styles.label}>아이디</label>
                <input
                  type="text"
                  value={editUsername}
                  onChange={(e) => setEditUsername(e.target.value)}
                  style={styles.input}
                />
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>표시 이름</label>
              <input
                type="text"
                value={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.value)}
                style={styles.input}
              />
            </div>
            {isAdministrator && (
              <div style={styles.formGroup}>
                <label style={styles.label}>역할</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as 'administrator' | 'manager')}
                  style={styles.select}
                  disabled={selectedAdmin.admin_id === currentUser.admin_id}
                >
                  <option value="manager">매니저</option>
                  <option value="administrator">총괄관리자</option>
                </select>
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>새 비밀번호</label>
              <input
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                style={styles.input}
                placeholder="변경 시에만 입력"
              />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowEditModal(false)}>
                취소
              </button>
              <button style={styles.submitButton} onClick={handleUpdateAdmin}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  actions: {
    marginBottom: spacing[5],
  },
  addButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
  },
  tableContainer: {
    ...commonStyles.tableContainer,
  },
  table: {
    ...commonStyles.table,
  },
  th: {
    ...commonStyles.th,
  },
  td: {
    ...commonStyles.td,
  },
  emptyState: {
    ...commonStyles.emptyState,
  },
  roleAdmin: {
    color: colors.status.info,
    fontWeight: typography.fontWeight.medium,
  },
  roleManager: {
    color: colors.gray[600],
  },
  statusActive: {
    color: colors.status.success,
    fontWeight: typography.fontWeight.medium,
  },
  statusInactive: {
    color: colors.status.error,
    fontWeight: typography.fontWeight.medium,
  },
  actionButtons: {
    display: 'flex',
    gap: spacing[2],
  },
  editButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  toggleButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  deleteButton: {
    ...commonStyles.button,
    ...commonStyles.buttonDanger,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: colors.white,
    padding: spacing[8],
    minWidth: '400px',
    maxWidth: '500px',
    border: `1px solid ${colors.gray[200]}`,
  },
  modalTitle: {
    ...commonStyles.sectionTitle,
    marginBottom: spacing[6],
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
    marginBottom: spacing[5],
  },
  label: {
    ...commonStyles.label,
    minWidth: 'auto',
  },
  input: {
    ...commonStyles.input,
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    ...commonStyles.select,
    width: '100%',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: spacing[3],
    marginTop: spacing[6],
  },
  cancelButton: {
    ...commonStyles.button,
    ...commonStyles.buttonSecondary,
  },
  submitButton: {
    ...commonStyles.button,
    ...commonStyles.buttonPrimary,
  },
};

export default AdminAccountTab;
